import { Component, computed, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import {
  ListboxComponent,
  ListboxConfig,
  ListboxItem,
  ListboxTab,
} from '@groundworks/ngx-groundworks-ui';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../config.service';
import { BranchInfo } from '../../sdk/sdk';
import { BranchService } from '../../services/branch.service';
import { DefaultBranchService } from '../../services/default-branch.service';
import { OpportunityRevisitsSignalRService } from '../../services/opportunity-revisits-signalr.service';
import { QuoteService } from '../../services/quote.service';
import { QuotesSignalRService } from '../../services/quotes-signalr.service';
import { removeListboxItemById } from '../../utils/listbox.util';
import { SideModalComponent } from '../side-modal/side-modal.component';

@Component({
  selector: 'app-quotes-listbox',
  imports: [ListboxComponent, SideModalComponent],
  templateUrl: './quotes-listbox.component.html',
})
export class QuotesListboxComponent implements OnInit, OnDestroy {
  @ViewChild('sideModal') sideModalComponent!: SideModalComponent;
  readonly configService = inject(ConfigService);
  private readonly quotesService = inject(QuoteService);
  private readonly branchService = inject(BranchService);
  private readonly defaultBranchService = inject(DefaultBranchService);
  private readonly destroy$ = new Subject<void>();
  private readonly removedOpportunityIds = new Set<string>();
  private readonly quotesSalesSignalRService = inject(QuotesSignalRService);
  private readonly opportunityRevisitsSignalRService = inject(OpportunityRevisitsSignalRService);

  quotes: ListboxItem[] = [];
  serviceQuotes: ListboxItem[] = [];
  selectedBranches = signal<BranchInfo[]>([]);
  startDate = signal<Date | undefined>(undefined);
  endDate = signal<Date | undefined>(undefined);
  completedSales: ListboxItem[] = [];

  readonly enableService = this.configService.enableService;

  tabs = signal<ListboxTab[]>([]);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  showSettings = signal(true);
  hasLoadedData = signal(false);

  tableConfig: ListboxConfig = {
    columns: [
      { header: 'Branch', field: 'branch', sortable: true },
      { header: 'Customer Name', field: 'name', sortable: true },
      { header: 'Quote Date', field: 'quoteDate', sortable: true, dataType: 'date' },
      { header: 'Quote Amount', field: 'quoteAmount', sortable: true, dataType: 'currency' },
      { header: 'CFI / Sales Rep', field: 'cfiName', sortable: true },
      { header: 'Appointment Date', field: 'appointmentDate', sortable: true, dataType: 'date' },
      { header: 'Dispo', field: 'dispo', sortable: true },
      { header: 'Sub Dispo', field: 'subDispo', sortable: true },
      { header: 'Actions', field: 'action', sortable: false },
    ],
  };

  serviceTableConfig: ListboxConfig = {
    columns: [
      { header: 'Branch', field: 'branch', sortable: true },
      { header: 'Customer Name', field: 'name', sortable: true },
      { header: 'Quote Date', field: 'quoteDate', sortable: true, dataType: 'date' },
      { header: 'Quote Amount', field: 'quoteAmount', sortable: true, dataType: 'currency' },
      { header: 'Service Tech', field: 'cfiName', sortable: true },
      { header: 'Appointment Date', field: 'appointmentDate', sortable: true, dataType: 'date' },
      { header: 'Dispo', field: 'dispo', sortable: true },
      { header: 'Actions', field: 'action', sortable: false },
    ],
  };

  readonly noDataMessage = computed(() => {
    if (!this.selectedBranches() || !this.hasLoadedData()) {
      return 'Please select a branch';
    }
    return 'No records found';
  });

  private initialLoadHasHappened = false;
  private hasDefaultBranch = signal(false);

  handleAction(actionValue: string): void {
    window.open(actionValue, '_blank');
  }

  handleSettingsClick(): void {
    this.sideModalComponent.open(this.sideModalComponent.sideModalTemplate);
  }

  onFiltersChanged(filters: { startDate?: Date; endDate?: Date }): void {
    this.startDate.set(filters.startDate);
    this.endDate.set(filters.endDate);
    this.loadQuotes();
    this.initialLoadHasHappened = true;
  }

  ngOnInit(): void {
    this.defaultBranchService.defaultBranch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasDefault) => {
        this.hasDefaultBranch.set(hasDefault);
        if (hasDefault && this.selectedBranches().length > 0 && !this.initialLoadHasHappened) {
          this.loadQuotes();
          this.initialLoadHasHappened = true;
        }
      });

    this.branchService
      .getSelectedBranches()
      .pipe(takeUntil(this.destroy$))
      .subscribe((branch) => {
        this.handleBranchSelectionChange(branch);
      });

    this.quotesSalesSignalRService.startConnection();
    this.quotesSalesSignalRService.opportunityDeactivated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId) => {
        this.deactivateOpportunity(opportunityId);
      });

    this.opportunityRevisitsSignalRService.startConnection();
    this.opportunityRevisitsSignalRService.opportunityUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId) => {
        this.deactivateOpportunity(opportunityId);
      });
  }

  private handleBranchSelectionChange(branches: BranchInfo[] | null) {
    if (branches) {
      this.selectedBranches.set(branches);
      this.hasLoadedData.set(false);

      if (!this.initialLoadHasHappened && this.hasDefaultBranch()) {
        this.loadQuotes();
        this.initialLoadHasHappened = true;
      }
    } else {
      this.selectedBranches.set([]);
      this.hasLoadedData.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.opportunityRevisitsSignalRService.stopConnection();
  }

  private loadQuotes(): void {
    const branches = this.selectedBranches();
    if (branches.length === 0) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.quotesService
      .getQuotes(branches.map((branch) => branch.id!), this.startDate(), this.endDate())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.removedOpportunityIds.clear();
          this.quotes =
            data.quotesReview?.map((quote) => ({
              id: quote.id,
              branch: quote.branch,
              name: quote.customerName,
              quoteDate: quote.quoteDate,
              quoteAmount: quote.quoteAmount,
              cfiName: quote.cfiName,
              appointmentDate: quote.appointmentDate,
              dispo: quote.disposition,
              subDispo: quote.subDisposition,
              action: `${this.configService.appBaseUrl}/quote-review?opportunityId=${quote.id}`,
            })) ?? [];
          this.serviceQuotes = this.enableService
            ? (data.serviceQuotesReview?.map((quote) => ({
                id: quote.id,
                branch: quote.branch,
                name: quote.customerName,
                quoteDate: quote.quoteDate,
                quoteAmount: quote.quoteAmount,
                cfiName: quote.cfiName,
                appointmentDate: quote.appointmentDate,
                dispo: quote.disposition,
                action: `${this.configService.appBaseUrl}/quote-review?opportunityId=${quote.id}`,
              })) ?? [])
            : [];
          this.updateTabs();
          this.hasLoadedData.set(true);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading quotes:', error);
          this.errorMessage.set('An error occurred while loading quotes. Please try again later.');
          this.isLoading.set(false);
        },
      });
  }

  private updateTabs(): void {
    const tabs: ListboxTab[] = [
      { id: 1, label: 'Sales Quotes to Review', config: this.tableConfig, items: this.quotes },
    ];
    if (this.enableService) {
      tabs.push({
        id: 2,
        label: 'Service Quotes to Review',
        config: this.serviceTableConfig,
        items: this.serviceQuotes,
      });
    }
    this.tabs.set(tabs);
  }

  private deactivateOpportunity(opportunityId: string) {
    if (!opportunityId || this.removedOpportunityIds.has(opportunityId)) {
      console.warn('Pending sale already removed:', opportunityId);
      return;
    }

    const removedFromQuotes = removeListboxItemById(this.quotes, opportunityId);
    const removedFromService = removeListboxItemById(this.serviceQuotes, opportunityId);

    if (removedFromQuotes || removedFromService) {
      this.removedOpportunityIds.add(opportunityId);
      this.updateTabs();
    }
  }
}
